import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { Media, MediaEvent, MediaEventType, MediaState, MediaType, Mote, MotesMediaPlayerPosition } from "../../models";
import { AutoSub, AutoSubOnDestroy } from "../../providers/auto-sub";
import { MotesMediaPlayerService } from "../media-player.service";
import { filter } from "rxjs";
import { Animations } from "../../animations";

@AutoSubOnDestroy()
@Component({
    selector: 'audio-player-wrapper',
    templateUrl: './audio-player-wrapper.component.html',
    styleUrls: ['./audio-player-wrapper.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.slide(300)]
})
export class AudioPlayerWrapper implements OnInit {

    /** 
     * The media and mote related to this audio-player-wrapper instance (e.g. in the Story page),
     * which might differ from the media actually playing.
     * This makes is possible to start a new media, while another is playing.
     * If null, this audio-player-wrapper is completely dependent on media events (the media actually playing).
     */
    @Input() relatedMedia: { mote: Mote, media: Media } = null;
    /** The audio player position ('top', 'bottom', 'static')  */
    @Input() position: MotesMediaPlayerPosition = MotesMediaPlayerPosition.BOTTOM;
    /** Set false to exclude mote order in simple player */
    @Input() showMoteOrder: boolean = true;
    /** Output click on simple player box */
    @Output() simplePlayerClicked = new EventEmitter<Mote>();

    public mote: Mote = null;
    public mediaPlaying: Media = null;
    private usingExternalAudio: boolean = false;
    private lastState: MediaState = MediaState.NONE;

    public paused: boolean = true;    
    public progress: number = 0;
    public percentPlayed: number = 0;
    public bufferPct = 0;
    public duration = 0;
    public currentTime = 0;
    public timeLeft = 0;

    // Expose to view
    public MotesMediaPlayerPosition = MotesMediaPlayerPosition;
    public MediaType = MediaType;

    constructor(private mediaPlayerService: MotesMediaPlayerService) { }

    ngOnInit(): void {

        AutoSub(this).reg['mediaEv'] = this.mediaPlayerService.watchMediaEvents().pipe(filter((ev) => this.isSound(ev))).subscribe((event: MediaEvent) => {

            this.mote = (event.state == MediaState.NONE) ? null : event.mote;
            this.mediaPlaying = (event.state == MediaState.NONE) ? null : event.media;

            // If the media currently playing is different from the related media, reset this wrapper (but not audio)
            if(!this.mediaPlaying || (this.relatedMedia && this.relatedMedia.media.key != this.mediaPlaying?.key)) {
                this.updateProgress(0, 0, 0, 0);
                this.paused = true;
                return;
            }

            if(event.type == MediaEventType.PROGRESSUPDATE || event.type == MediaEventType.LOAD_PROGRESS) {
                this.updateProgress(event.progress, event.state, event.bufferPct, event.duration);
            }
            else if(event.type == MediaEventType.STATEUPDATE) {
                this.paused = event.state == MediaState.PAUSED;
            }
        });

        AutoSub(this).reg['externalPlayerSub'] = this.mediaPlayerService.watchExternalMediaPlayer().subscribe((externalAudioPlayer) => {
            this.usingExternalAudio = Boolean(externalAudioPlayer);
        })
    }

    private isSound(ev: MediaEvent): boolean {
        return ev && ev.media && (ev.media.type == MediaType.SOUND || ev.media.type == MediaType.AMBISONIC);
    }

    private updateProgress(progress, state, bufferPct, duration) {
        this.progress = progress;
        this.lastState = state;
        this.paused = state == MediaState.PAUSED;
        this.bufferPct = bufferPct;
        this.percentPlayed = Math.round(progress * 10) / 10;
        this.duration = duration;
        this.currentTime = (this.percentPlayed / 100) * this.duration;
        if(this.duration == Infinity) return this.timeLeft = Infinity;
        else this.timeLeft = (isFinite(this.duration)) ? (this.duration - this.currentTime) : this.currentTime;
    }

    public pauseAudio(ev: MouseEvent): void {
        ev.stopPropagation();
        this.mediaPlayerService.pausePlayer();
    }

    public playAudio(ev: MouseEvent): void {
        ev.stopPropagation();
        // Start audio with related media if applicable, and otherwise the currently playing media
        this.mediaPlayerService.startPlayer(this.relatedMedia?.mote || this.mote, this.relatedMedia?.media || this.mediaPlaying);
        // Indicate that media is loading if its not already buffered 100%
        if(this.bufferPct < 100) {
            this.mediaPlayerService.setLoadingStatus("Loading...");
        }
    }

    public simplePlayerClick(ev: MouseEvent) {
        ev.stopPropagation();
        this.simplePlayerClicked.emit(this.mote);
    }

    public skipAudio(ev: MouseEvent, timeToSkip: number): void {
        ev.stopPropagation();
        const newTime = Math.max(this.currentTime + timeToSkip, 0);
        // TODO: Remove when native app seek to 0 has been fixed (native app currently do not send any update when seeking to 0)
        if(newTime == 0) setTimeout( () => this.updateProgress(0, this.lastState, this.bufferPct, this.duration), 100);
        this.mediaPlayerService.setTime(newTime);
    }

    public loadBarPress(ev, audioBar: HTMLElement) {
        if(!this.usingExternalAudio && (this.duration <= 0)) return;
        const audioBarRect = audioBar.getBoundingClientRect();
        let pct = (ev.clientX - audioBarRect.x) / audioBar.clientWidth;
        let seekSec = Math.floor(this.duration * pct);
        this.setTime(seekSec);
    }

    private setTime(seekSec: number = 10): void {
        this.mediaPlayerService.setTime(seekSec);
    }

}
